import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, RefreshCw, Bot, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  fetchAiUsageOverview,
  type UsagePeriod,
  type UsageBucket,
} from '@/lib/ai-usage';

const money = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
const periods: Array<{ value: UsagePeriod; label: string }> = [
  { value: 'month', label: 'This month' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];
function Breakdown({ rows }: { rows: UsageBucket[] }) {
  return rows.length ? (
    <ul className="space-y-3">
      {rows.map((row) => (
        <li
          key={row.name}
          className="flex flex-wrap items-center justify-between gap-2 text-sm"
        >
          <span className="min-w-0 break-words text-text-secondary">
            {row.name}{' '}
            <span className="text-text-tertiary">
              · {row.requests.toLocaleString()} entries
            </span>
          </span>
          <span className="font-medium tabular-nums">{money(row.costUsd)}</span>
        </li>
      ))}
    </ul>
  ) : (
    <p className="text-sm text-text-tertiary">
      No recorded usage in this period.
    </p>
  );
}
export function AiUsageOverview() {
  const [period, setPeriod] = useState<UsagePeriod>('month');
  const query = useQuery({
    queryKey: ['ai-usage-overview', period],
    queryFn: () => fetchAiUsageOverview(period),
    refetchInterval: 60000,
  });
  const report = query.data;
  const crm = report?.crm.status === 'available' ? report.crm.data : undefined;
  const wa =
    report?.whatsapp.status === 'available' ? report.whatsapp.data : undefined;
  const waCost = wa
    ? wa.source === 'provider'
      ? wa.costUsd
      : period === 'month'
        ? (wa.monthToDateCostUsd ?? undefined)
        : wa.costUsd
    : undefined;
  const combined =
    crm && waCost !== undefined ? crm.costUsd + waCost : undefined;
  const dates = [
    ...new Set([
      ...(crm?.byDay ?? []).map((day) => day.date),
      ...(wa?.byDay ?? []).map((day) => day.date),
      ...(wa?.unpricedDays ?? []),
    ]),
  ].sort();
  return (
    <div className="space-y-6" data-testid="combined-ai-usage">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">AI usage &amp; costs</h2>
          <p className="mt-1 text-sm text-text-secondary">
            WhatsApp recruitment and all recorded CRM AI activity, in one place.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>
      <div className="flex flex-wrap gap-2" aria-label="Usage period">
        {periods.map((item) => (
          <Button
            key={item.value}
            variant={period === item.value ? 'default' : 'outline'}
            size="sm"
            aria-pressed={period === item.value}
            onClick={() => setPeriod(item.value)}
          >
            {item.label}
          </Button>
        ))}
      </div>
      {query.isPending && (
        <p role="status" className="text-text-secondary">
          Loading AI usage…
        </p>
      )}
      {query.isError && (
        <div
          role="alert"
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm"
        >
          AI usage could not be refreshed.{' '}
          {report
            ? 'The last retrieved figures are shown below.'
            : 'No costs are assumed to be zero.'}{' '}
          Please retry.
        </div>
      )}
      {report && (
        <>
          <p className="text-xs text-text-tertiary">
            {report.periodStart.slice(0, 10)} – {report.periodEnd.slice(0, 10)}{' '}
            · UTC · Updated {new Date(query.dataUpdatedAt).toLocaleTimeString()}
          </p>
          <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-3">
            {[
              {
                label: 'Combined known cost',
                value: combined,
                note: 'WhatsApp + CRM; available sources only',
              },
              {
                label: 'WhatsApp agent',
                value: waCost,
                note:
                  wa?.source === 'provider'
                    ? 'Provider-reported usage, attributed to this agent'
                    : period === 'month'
                      ? 'Reconciled opening + recorded requests'
                      : 'Requests recorded since tracking began',
              },
              {
                label: 'CRM AI',
                value: crm?.costUsd,
                note: 'Assistant + candidate matching',
              },
            ].map((item) => (
              <Card key={item.label}>
                <CardContent className="p-5">
                  <p className="text-sm text-text-secondary">{item.label}</p>
                  <p className="my-2 text-3xl font-semibold tabular-nums">
                    {item.value === undefined
                      ? 'Unavailable'
                      : money(item.value)}
                  </p>
                  <p className="text-xs text-text-tertiary">{item.note}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-sm text-text-secondary">
            {report.costBasis}{' '}
            {report.providerAccountMonthlyLimitUsd != null && (
              <>
                Configured provider account limit:{' '}
                {money(report.providerAccountMonthlyLimitUsd)}/month, shared
                across projects.{' '}
              </>
            )}
            No additional application monthly cap is applied.
          </p>
          {wa?.source === 'provider' && (
            <div
              className="rounded-lg border border-white/10 p-4 text-sm text-text-secondary"
              role={wa.stale || wa.unpricedDays?.length ? 'alert' : undefined}
            >
              <p>
                WhatsApp costs include historical usage and are allocated from
                Anthropic’s daily billing reports using this agent’s share of
                each matching token category. They are not the old cumulative
                agent total. Periods use UTC calendar days, including today.
              </p>
              <p className="mt-2">
                Provider data retrieved{' '}
                {wa.costUpdatedAt
                  ? new Date(wa.costUpdatedAt).toLocaleString()
                  : 'recently'}
                . Provider reporting usually lags by several minutes; it is not
                an invoice-final figure.
              </p>
              {wa.stale && (
                <p className="mt-2 text-amber-400">
                  Provider refresh is delayed. Showing the last successful
                  report.
                </p>
              )}
              {!!wa.unpricedDays?.length && (
                <p className="mt-2 text-amber-400">
                  Partial total: billing has not reconciled{' '}
                  {wa.unpricedDays.join(', ')} yet. Those days are excluded.
                </p>
              )}
            </div>
          )}
          {(report.crm.status === 'unavailable' ||
            report.whatsapp.status === 'unavailable') && (
            <div
              role="alert"
              className="flex gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm"
            >
              <AlertCircle className="h-5 w-5 shrink-0" />
              <div>
                {report.crm.status === 'unavailable' && (
                  <p>{report.crm.reason}</p>
                )}
                {report.whatsapp.status === 'unavailable' && (
                  <p>{report.whatsapp.reason}</p>
                )}
                <p className="mt-1">
                  The combined total is unavailable until both sources respond.
                </p>
              </div>
            </div>
          )}
          <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="min-w-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="h-5 w-5 text-emerald-500" />
                  WhatsApp activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {wa && wa.statsAvailable !== false ? (
                  <>
                    <dl className="grid grid-cols-2 gap-4 text-sm">
                      {[
                        ['Incoming messages', wa.messages],
                        ['Active phone numbers', wa.activePhones],
                        ['Model requests', wa.requests],
                        ['Awaiting processing', wa.queueCount],
                        ['Needs review', wa.reviewCount],
                        ['Delivery failures', wa.deliveryStates.failed ?? 0],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <dt className="text-text-tertiary">{label}</dt>
                          <dd className="mt-1 text-xl font-semibold tabular-nums">
                            {Number(value).toLocaleString()}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <p className="text-xs text-text-tertiary">
                      Model: {wa.model}. Detailed activity starts{' '}
                      {wa.trackingStartedAt
                        ? new Date(wa.trackingStartedAt).toLocaleString()
                        : 'when the new tracker was activated'}
                      ; older message counts are not backfilled.
                    </p>
                    {wa.source !== 'provider' && (
                      <p className="text-sm">
                        This month’s reconciled opening:{' '}
                        <strong>{money(wa.monthOpeningCostUsd ?? 0)}</strong>.
                        Daily rows below show new recorded requests only.
                      </p>
                    )}
                    <p className="text-sm">
                      Unconfirmed request allowance:{' '}
                      <strong>{money(wa.uncertainReservedUsd ?? 0)}</strong> —
                      excluded from known cost until reconciled.
                    </p>
                    {(wa.circuitOpen || wa.applicationPeriodCapEnabled) && (
                      <p role="alert" className="text-sm text-amber-400">
                        {wa.circuitOpen
                          ? 'The accounting safety check has paused new AI requests. Operator review is needed.'
                          : 'An application spending limit is enabled in runtime configuration.'}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(wa.deliveryStates).map(
                        ([state, count]) => (
                          <span
                            className="rounded-md bg-white/5 px-2 py-1 text-xs text-text-secondary"
                            key={state}
                          >
                            {state}: {count}
                          </span>
                        ),
                      )}
                    </div>
                    <details className="text-sm">
                      <summary className="cursor-pointer text-text-secondary">
                        Token breakdown
                      </summary>
                      <dl className="mt-3 grid grid-cols-2 gap-2">
                        {[
                          ['Input', wa.inputTokens],
                          ['Output', wa.outputTokens],
                          ['Cache writes', wa.cacheWriteTokens],
                          ['Cache reads', wa.cacheReadTokens],
                        ].map(([label, value]) => (
                          <div key={label}>
                            <dt className="text-text-tertiary">{label}</dt>
                            <dd>{Number(value).toLocaleString()}</dd>
                          </div>
                        ))}
                      </dl>
                    </details>
                  </>
                ) : (
                  <div className="space-y-3 text-sm text-text-secondary">
                    <p>
                      {wa?.source === 'provider'
                        ? 'Costs and tokens come from Anthropic reporting. Message, queue and delivery statistics are not currently available from the agent tracker.'
                        : 'WhatsApp statistics are unavailable.'}
                    </p>
                    {wa?.source === 'provider' && (
                      <dl className="grid grid-cols-2 gap-4">
                        {[
                          ['Input tokens', wa.inputTokens],
                          ['Output tokens', wa.outputTokens],
                          ['Cache write tokens', wa.cacheWriteTokens],
                          ['Cache read tokens', wa.cacheReadTokens],
                        ].map(([label, value]) => (
                          <div key={label}>
                            <dt className="text-text-tertiary">{label}</dt>
                            <dd className="font-semibold">
                              {Number(value).toLocaleString()}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="min-w-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Bot className="h-5 w-5 text-emerald-500" />
                  CRM AI activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {crm ? (
                  <>
                    <dl className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <dt className="text-text-tertiary">Usage entries</dt>
                        <dd className="mt-1 text-xl font-semibold">
                          {crm.entries.toLocaleString()}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-text-tertiary">Recorded tokens</dt>
                        <dd className="mt-1 text-xl font-semibold">
                          {(
                            crm.inputTokens + crm.outputTokens
                          ).toLocaleString()}
                        </dd>
                      </div>
                    </dl>
                    <Breakdown rows={crm.byWorkload} />
                    <div className="border-t border-white/10 pt-4">
                      <h3 className="mb-3 text-sm font-medium">By model</h3>
                      <Breakdown rows={crm.byModel} />
                    </div>
                    {crm.unknownRequests > 0 && (
                      <p role="alert" className="text-sm text-amber-400">
                        {crm.unknownRequests}{' '}
                        {crm.unknownRequests === 1
                          ? 'request has'
                          : 'requests have'}{' '}
                        unconfirmed usage. Their costs are not included in the
                        known total.
                      </p>
                    )}
                    {crm.legacyEntries > 0 && (
                      <p className="text-xs text-text-tertiary">
                        Includes {crm.legacyEntries} older conversation-level
                        usage entries. Older estimates may omit failed calls or
                        use earlier pricing. New entries record each provider
                        response separately.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-text-tertiary">
                    CRM statistics are unavailable.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Daily recorded cost</CardTitle>
              <p className="text-xs text-text-tertiary">
                {wa?.source === 'provider'
                  ? 'WhatsApp: provider-attributed daily costs. CRM: recorded request costs.'
                  : 'Excludes the WhatsApp opening balance and unconfirmed requests.'}
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-text-tertiary">
                      <th className="py-2 text-left font-medium">Date (UTC)</th>
                      <th className="py-2 text-right font-medium">WhatsApp</th>
                      <th className="py-2 text-right font-medium">CRM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dates.map((date) => (
                      <tr key={date} className="border-b border-white/5">
                        <td className="py-2">{date}</td>
                        <td className="py-2 text-right tabular-nums">
                          {wa?.unpricedDays?.includes(date)
                            ? 'Pending billing'
                            : wa
                              ? money(
                                  wa.byDay.find((day) => day.date === date)
                                    ?.costUsd ?? 0,
                                )
                              : 'Unavailable'}
                        </td>
                        <td className="py-2 text-right tabular-nums">
                          {crm
                            ? money(
                                crm.byDay.find((day) => day.date === date)
                                  ?.costUsd ?? 0,
                              )
                            : 'Unavailable'}
                        </td>
                      </tr>
                    ))}
                    {dates.length === 0 && (
                      <tr>
                        <td
                          colSpan={3}
                          className="py-6 text-center text-text-tertiary"
                        >
                          No daily records are available for this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
